import React, { FC, useState } from 'react';
import { Policy, PolicyTemplate, Protocols } from '../../../../../types/policies';
import { Dropdown, RadioGroup, showAlert, SwitcherTab, TextInput } from '@silverfort/sf-components';
import { SwitcherTabProps } from '@silverfort/sf-components/dist/SwitcherTabs/SwitcherTabs';
import {
    AD_ENTITY_TYPES,
    DIRECTORY_TYPES,
    POLICY_ACTION_TYPES,
    POLICY_SELECT_ACCOUNT_OPTIONS,
    POLICY_SELECT_DESTINATION_OPTIONS,
    SELECTOR_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import ADServersSelector from '../../../../Controls/Modals/ADServersSelector/ADServersSelector';
import ADEntitiesSelector from '../../../../Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import IpsSelector from '../../../../Controls/Modals/IpsSelector/IpsSelector';
import { useAllowedTokens } from '../../../hooks/useAllowedTokens';
import IncludingUsersCheckboxes, { IncludingUsersCheckboxesProps } from '../../../components/IncludingUsersCheckboxes';
import {
    getActionSwitcherTabItems,
    getAllComputersObject,
    getUsersAndGroupsTooltip,
    getProtocolFieldTooltip,
    getProtocolRadioGroupOptions,
    getServersSelectedEntities,
    getServersSelectedLabels,
    getUnresolvedResourcesObject,
    isFieldEditable,
    mapSelectedServer,
    getSourceTooltip,
    getDestinationTooltip
} from '../../utils/forms';
import { PolicyConfiguration } from '../../../types/PolicyConfiguration';
import { FieldErrors, FieldName, PolicyFormValues } from '../../types/forms';
import { getActionDisclaimer } from '../../utils/forms';
import FormField from './components/FormField';
import ModalFormField from './components/ModalFormField';
import { allComputersResource, unresolvedResources } from '../../../common';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext';
import { isAuthenticationFirewallEnabled, isMfaUsersEnabled } from '../../../utils/utils';
import { ArrayElement } from '../../../../../types/types';

interface Props {
    template: PolicyTemplate;
    policyFormValues: PolicyFormValues;
    setPolicyFormValue: <F extends FieldName>(fieldName: F, value: PolicyFormValues[F]) => void;
    policyConfiguration: PolicyConfiguration;
    newPolicyData?: Policy;
    fieldErrors: FieldErrors;
}

const FormLayout: FC<Props> = ({
    template,
    policyFormValues,
    setPolicyFormValue,
    policyConfiguration,
    newPolicyData,
    fieldErrors
}) => {
    const [showEntitiesSelectorModal, setShowEntitiesSelectorModal] = useState(false);
    const [showHostsSelectorModal, setShowHostsSelectorModal] = useState(false);
    const [showServersSelectorModal, setShowServersSelectorModal] = useState(false);
    const [showServersIpsSelectorModal, setShowServersIpsSelectorModal] = useState(false);
    const [accountTypes, setAccountTypes] = useState(
        policyFormValues.account_types ?? newPolicyData?.account_types ?? [POLICY_SELECT_ACCOUNT_OPTIONS.USERS]
    );
    const { supportedActions } = template;

    const { pricingModulesState } = usePricingModulesState();
    const isAuthenticationModuleEnabled = isAuthenticationFirewallEnabled(pricingModulesState);
    const isMfaUsersModuleEnabled = isMfaUsersEnabled(pricingModulesState);

    const actionTypeSwitchItems: SwitcherTabProps<number>['items'] = getActionSwitcherTabItems(
        supportedActions,
        isAuthenticationModuleEnabled,
        isMfaUsersModuleEnabled
    );

    const allowedTokens = useAllowedTokens(
        policyFormValues.allowed_token_types ?? [],
        policyConfiguration.ConfiguredTokens,
        policyFormValues.desktop_enabled,
        policyFormValues.ping_fed_enabled,
        policyFormValues.mode_type,
        policyFormValues.protocol === Protocols.LDAP
    );

    const allComputersObject = getAllComputersObject(policyFormValues, policyConfiguration);
    const unresolvedResourcesObject = getUnresolvedResourcesObject(policyFormValues, policyConfiguration);

    const handleEntitiesClick = () => {
        setShowEntitiesSelectorModal(true);
    };

    const handleServersClick = () => {
        setShowServersSelectorModal(true);
    };

    const handleServersIpsClick = () => {
        setShowServersIpsSelectorModal(true);
    };

    const shouldRenderField = (fieldName: keyof PolicyFormValues) => {
        return isFieldEditable(fieldName, template, policyFormValues);
    };

    const handlePolicyNameChange = (value: string) => {
        setPolicyFormValue('policyName', value);
    };

    const addUnresolvedResources = () => {
        setPolicyFormValue('unresolved_resources_match_all_services', true);
        setPolicyFormValue('unresolved_resources_services', []);
    }

    const handleProtocolChange = (value: Protocols) => {
        if (value === Protocols.KERBEROS || value === Protocols.KERBEROS_NTLM || value === Protocols.NTLM) {
            if (!policyFormValues.destination_types || policyFormValues.destination_types.length === 0) {
                setPolicyFormValue('destination_types', [POLICY_SELECT_DESTINATION_OPTIONS.SERVERS]);
            } else if (!policyFormValues.destination_types.includes(POLICY_SELECT_DESTINATION_OPTIONS.SERVERS)) {
                setPolicyFormValue('destination_types', [...policyFormValues.destination_types, POLICY_SELECT_DESTINATION_OPTIONS.SERVERS]);
            }
        } else {
            setPolicyFormValue('destination_types', []);
        }
        setPolicyFormValue('protocol', value);
    };

    const saveServers = (selectedServers: PolicyFormValues['servers']) => {
        const newServers: PolicyFormValues['servers'] = []
        const areAllServersExcluded = selectedServers?.every(
            (server) => server.excluded || server.guid === allComputersResource.guid || server.guid === unresolvedResources.guid
        );
        let destinationTypes: PolicyFormValues['destination_types'] = []
        if (policyFormValues.protocol === Protocols.KERBEROS_NTLM || policyFormValues.protocol === Protocols.KERBEROS || policyFormValues.protocol === Protocols.NTLM) {
            destinationTypes = [POLICY_SELECT_DESTINATION_OPTIONS.SERVERS]
        }

        let allComputers = null
        let unresolved: ArrayElement<PolicyFormValues['servers']> | null = null
        if (selectedServers) {
            for (const server of selectedServers) {
                if (server.guid === allComputersResource.guid) {
                    allComputers = server
                    continue;
                }
                if (server.guid === unresolvedResources.guid) {
                    unresolved = server
                    continue;
                }
                newServers.push(mapSelectedServer(server, areAllServersExcluded ? template.spns : undefined))
            }
        }

        if (allComputers) {
            const protectSpecificSpns = template.spns && template.spns.length > 0;
            if (protectSpecificSpns) {
                setPolicyFormValue('match_all_services', false);
                setPolicyFormValue('services', template.spns);
            } else {
                setPolicyFormValue('match_all_services', true);
                setPolicyFormValue('services', []);
            }
        } else {
            setPolicyFormValue('match_all_services', false);
            setPolicyFormValue('services', []);
        }
        if (unresolved) {
            destinationTypes.push(POLICY_SELECT_DESTINATION_OPTIONS.UNRESOLVED)
            addUnresolvedResources()
        }
        setPolicyFormValue('destination_types', destinationTypes);
        setPolicyFormValue('servers', newServers);
        setShowServersSelectorModal(false);
    };

    const saveEntities = (selectedEntities: any) => {
        setPolicyFormValue('account_types', accountTypes);
        setPolicyFormValue('entities', selectedEntities.objectEntities);
        setShowEntitiesSelectorModal(false);
    };

    const isKerberosProtocolEnabled =
        policyFormValues.protocol === Protocols.KERBEROS_NTLM || policyFormValues.protocol === Protocols.KERBEROS;

    const saveDevices = (selectedDevices: any) => {
        if (isKerberosProtocolEnabled) {
            setPolicyFormValue('hosts_ips', selectedDevices.ipEntities);
            setPolicyFormValue('unmanaged_hosts', selectedDevices.unmanagedEntities);
        }
        const newDevices = selectedDevices.objectEntities.map((host) => {
            return {
                type: host.type === AD_ENTITY_TYPES.SERVER ? AD_ENTITY_TYPES.HOST : host.type,
                guid: host.guid,
                name: host.name,
                path: host.path,
                excluded: host.excluded,
                domain_name: host.domain_name,
                is_deleted: host.is_deleted
            };
        });

        setPolicyFormValue('hosts', newDevices);
        setShowHostsSelectorModal(false);
    };

    const saveIps = (serversIps: PolicyFormValues['servers_ips']) => {
        setPolicyFormValue('servers_ips', serversIps);
        setShowServersIpsSelectorModal(false);
    };

    const handleChangeActionType = (actionType: PolicyFormValues['action_type']) => {
        setPolicyFormValue('action_type', actionType);
        if (actionType === POLICY_ACTION_TYPES.MFA.value && template.mfaNote) {
            showAlert(template.mfaNote, 'info', {
                title: 'MFA'
            });
        }
    };

    const handleChangeDisplayName = (value: string) => {
        setPolicyFormValue('display_name', value);
    };

    const handleChangeTokens = (values?: number | number[]) => {
        setPolicyFormValue('allowed_token_types', typeof values === 'number' ? [values] : values);
    };

    const onIncludeAccountTypeChanged: IncludingUsersCheckboxesProps['onIncludeAccountTypeChanged'] = (
        event,
        accountType
    ) => {
        if (event.target.checked) {
            setAccountTypes((currentAccountTypes) => [...currentAccountTypes, accountType]);
        } else {
            setAccountTypes((currentAccountTypes) => currentAccountTypes.filter((type) => type !== accountType));
        }
    };

    const getErrorMessageForField = (field: FieldName) => fieldErrors[field]?.[0];

    const allowedTokensOptions = allowedTokens.map((token) => ({
        value: token.value,
        title: token.label,
        disabled: token.disabled
    }));

    return (
        <div className="w-[560px] flex flex-col gap-4">
            <FormField
                mandatory
                label="Policy name"
                Input={
                    <TextInput
                        className="w-80"
                        value={policyFormValues.policyName}
                        onChange={handlePolicyNameChange}
                        validationMessage={getErrorMessageForField('policyName')}
                    />
                }
            />
            {shouldRenderField('protocol') && policyFormValues.protocol && (
                <FormField
                    mandatory
                    label="Protocol"
                    tooltipInfo={getProtocolFieldTooltip(template)}
                    Input={
                        <div className="flex flex-col gap-[6px]">
                            <RadioGroup
                                containerClassName="w-4 leading-4"
                                options={getProtocolRadioGroupOptions(template)}
                                selectedOption={policyFormValues.protocol}
                                //@ts-ignore TODO: this issue might be solved in the components library since it doesnt know how to get generics (and we want type of closed values here)
                                onChange={handleProtocolChange}
                                validationMessage={getErrorMessageForField('protocol')}
                            />
                            {template.protocolEntitiesSelectionTooltip && <span className="font-sf text-13 leading-[15.51px] text-sf-gray-500 w-[337px] h-4">
                                {template.protocolEntitiesSelectionTooltip}
                            </span>}
                        </div>
                    }
                />
            )}
            {shouldRenderField('entities') && (
                <ModalFormField
                    mandatory
                    label="Users and Groups"
                    showModal={showEntitiesSelectorModal}
                    selectedLabels={policyFormValues.entities?.map((entity) => entity.name)}
                    onClick={handleEntitiesClick}
                    errorMessage={getErrorMessageForField('entities')}
                    tooltipInfo={getUsersAndGroupsTooltip(template)}
                    Modal={
                        <ADEntitiesSelector
                            isEditor
                            selectedObjectEntities={policyFormValues.entities || []}
                            selectorType={SELECTOR_TYPES.ENTITIES}
                            showModal={showEntitiesSelectorModal}
                            handleCloseModal={() => {
                                setAccountTypes(policyFormValues.account_types ?? []);
                                setShowEntitiesSelectorModal(false);
                            }}
                            saveEntities={saveEntities}
                            modalTitle={'Protect Users And Groups'}
                            showExcluded={true}
                            authTypes={[DIRECTORY_TYPES.ad.key]}
                            footerComponent={
                                <IncludingUsersCheckboxes
                                    accountTypes={accountTypes}
                                    onIncludeAccountTypeChanged={onIncludeAccountTypeChanged}
                                />
                            }
                            // TODO: this is because the component is in JS
                            footerMsg={undefined}
                            allowIps={undefined}
                            allowApplications={undefined}
                            disableNetMasks={undefined}
                            allowUnmanagedEntities={undefined}
                            selectedIpEntities={undefined}
                            selectedApplicationEntities={undefined}
                            selectedUnmanagedEntities={undefined}
                            removeEntitiesBlocked={undefined}
                            showSelectAll={undefined}
                            allowSelectAll={undefined}
                            selectAllText={undefined}
                            allEntitiesSelected={undefined}
                            maxEntitySelection={undefined}
                            maxFileSize={undefined}
                            maxFileSizeText={undefined}
                            ipOnly={false}
                            disableAutoComplete={false}
                        />
                    }
                />
            )}
            {shouldRenderField('hosts') && (
                <ModalFormField
                    mandatory
                    label="Source"
                    showModal={showHostsSelectorModal}
                    selectedLabels={policyFormValues.hosts?.map((host) => host.name)}
                    onClick={() => setShowHostsSelectorModal(true)}
                    errorMessage={getErrorMessageForField('hosts')}
                    tooltipInfo={getSourceTooltip(template)}
                    Modal={
                        <ADEntitiesSelector
                            selectedObjectEntities={policyFormValues.hosts || []}
                            selectedIpEntities={policyFormValues.hosts_ips || []}
                            selectedUnmanagedEntities={policyFormValues.unmanaged_hosts || []}
                            selectorType={SELECTOR_TYPES.DEVICES}
                            showModal={showHostsSelectorModal}
                            handleCloseModal={() => setShowHostsSelectorModal(false)}
                            saveEntities={saveDevices}
                            showExcluded={true}
                            allowIps={isKerberosProtocolEnabled}
                            allowUnmanagedEntities={isKerberosProtocolEnabled}
                            isEditor={true}
                            modalTitle={'Select Devices'}
                            authTypes={[DIRECTORY_TYPES.ad.key]}
                            // TODO: this is because the component is in JS
                            footerMsg={undefined}
                            allowApplications={undefined}
                            disableNetMasks={undefined}
                            selectedApplicationEntities={undefined}
                            removeEntitiesBlocked={undefined}
                            showSelectAll={undefined}
                            allowSelectAll={undefined}
                            selectAllText={undefined}
                            allEntitiesSelected={undefined}
                            maxEntitySelection={undefined}
                            maxFileSize={undefined}
                            maxFileSizeText={undefined}
                            ipOnly={false}
                            disableAutoComplete={false}
                            footerComponent={undefined}
                        />
                    }
                />
            )}
            {shouldRenderField('servers') && (
                <ModalFormField
                    mandatory
                    label="Destination"
                    showModal={showServersSelectorModal}
                    selectedLabels={getServersSelectedLabels(policyFormValues.servers)}
                    onClick={handleServersClick}
                    errorMessage={getErrorMessageForField('servers')}
                    tooltipInfo={getDestinationTooltip(template)}
                    Modal={
                        <ADServersSelector
                            isEditor
                            showServices={false}
                            modalTitle={undefined}
                            selectedEntities={getServersSelectedEntities(policyFormValues.servers, allComputersObject, unresolvedResourcesObject, policyFormValues.destination_types)}
                            showModal={showServersSelectorModal}
                            handleCloseModal={() => setShowServersSelectorModal(false)}
                            saveEntities={saveServers}
                            showExcluded={true}
                            GlobalServices={policyConfiguration.GlobalServices}
                            allComputersObject={allComputersObject}
                            unresolvedResourcesObject={unresolvedResourcesObject}
                            showUnresolvedResources={policyFormValues.protocol === Protocols.KERBEROS_NTLM || policyFormValues.protocol === Protocols.KERBEROS || policyFormValues.protocol === Protocols.NTLM}
                        />
                    }
                />
            )}
            {shouldRenderField('servers_ips') && (
                <ModalFormField
                    mandatory
                    label="Application IP"
                    showModal={showServersIpsSelectorModal}
                    selectedLabels={policyFormValues.servers_ips?.map((server_ip) => server_ip.address)}
                    onClick={handleServersIpsClick}
                    errorMessage={getErrorMessageForField('servers_ips')}
                    Modal={
                        <IpsSelector
                            isEditor
                            selectedIps={policyFormValues.servers_ips || []}
                            showModal={showServersIpsSelectorModal}
                            handleCloseModal={() => setShowServersIpsSelectorModal(false)}
                            saveIps={saveIps}
                            showExcluded={false}
                            modalTitle={undefined}
                            maxIps={undefined}
                            maxFileSize={undefined}
                            maxFileSizeText={undefined}
                        />
                    }
                />
            )}
            {shouldRenderField('action_type') && (
                <FormField
                    mandatory
                    label="Action"
                    tooltipInfo={getActionDisclaimer(template)}
                    Input={
                        <SwitcherTab
                            tabClassName="flex-none"
                            items={actionTypeSwitchItems}
                            selectedItem={policyFormValues.action_type || actionTypeSwitchItems[0].value}
                            onSelect={handleChangeActionType}
                        />
                    }
                />
            )}
            {shouldRenderField('display_name') && (
                <FormField
                    mandatory
                    label="MFA Prompt display name"
                    Input={
                        <TextInput
                            className="w-80"
                            value={policyFormValues.display_name}
                            onChange={handleChangeDisplayName}
                            validationMessage={getErrorMessageForField('display_name')}
                        />
                    }
                />
            )}
            {shouldRenderField('allowed_token_types') && (
                <FormField
                    mandatory
                    label="Tokens"
                    Input={
                        <Dropdown
                            multiple
                            className="w-80"
                            text="Select"
                            variant="trigger"
                            items={allowedTokensOptions}
                            selectedItems={policyFormValues.allowed_token_types ?? []}
                            onChange={handleChangeTokens}
                            validationMessage={getErrorMessageForField('allowed_token_types')}
                        />
                    }
                />
            )}
        </div>
    );
};

export default FormLayout;
